// 총판 데스크 전용 UI 프리미티브 — 정제된 운영 콘솔 룩.
// 뉴트럴 슬레이트 + 단일 인디고 액센트, 하이라인 구분선, 소프트 섀도, tabular-nums.
// 주의(preflight OFF): global.css의 bare button/input/select 전역 스타일을 이기려면
// 반드시 클래스로 스타일한다(클래스 특이성 > 엘리먼트). @/flow/ui 대신 이것을 쓴다.
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

export const cx = (...xs: (string | false | null | undefined)[]): string =>
  xs.filter(Boolean).join(" ");

export type Tone = "neutral" | "accent" | "success" | "warn" | "danger" | "info";

// ── 표면 ────────────────────────────────────────────────────────────────────
export function Card({
  className,
  children,
  as: As = "div",
}: {
  className?: string;
  children: ReactNode;
  as?: "div" | "section";
}): JSX.Element {
  return (
    <As
      className={cx(
        "rounded-xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
        className,
      )}
    >
      {children}
    </As>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/70 pb-4">
      <div className="min-w-0">
        {eyebrow && (
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-indigo-500">
            {eyebrow}
          </div>
        )}
        <h1 className="m-0 text-[19px] font-semibold leading-tight tracking-[-0.01em] text-slate-900">
          {title}
        </h1>
        {subtitle && <p className="mb-0 mt-1.5 text-[13px] leading-relaxed text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={cx("text-[11px] font-semibold uppercase tracking-[0.09em] text-slate-400", className)}>
      {children}
    </div>
  );
}

// ── 지표 카드 ────────────────────────────────────────────────────────────────
const STAT_INK: Record<Tone, string> = {
  neutral: "text-slate-900",
  accent: "text-indigo-600",
  success: "text-emerald-600",
  warn: "text-amber-600",
  danger: "text-rose-600",
  info: "text-sky-600",
};
const STAT_BAR: Record<Tone, string> = {
  neutral: "bg-slate-200",
  accent: "bg-indigo-500",
  success: "bg-emerald-400",
  warn: "bg-amber-400",
  danger: "bg-rose-400",
  info: "bg-sky-400",
};

export function StatCard({
  label,
  value,
  unit,
  hint,
  tone = "neutral",
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}): JSX.Element {
  return (
    <Card className="relative overflow-hidden px-4 py-3.5">
      <span className={cx("absolute inset-x-0 top-0 h-[3px]", STAT_BAR[tone])} />
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className={cx("tnum text-[24px] font-semibold leading-none tracking-[-0.02em]", STAT_INK[tone])}>
          {value}
        </span>
        {unit && <span className="text-xs font-medium text-slate-400">{unit}</span>}
      </div>
      {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </Card>
  );
}

// ── 뱃지 / 상태 ──────────────────────────────────────────────────────────────
const BADGE: Record<Tone, string> = {
  neutral: "bg-slate-100 text-slate-600 ring-slate-500/10",
  accent: "bg-indigo-50 text-indigo-700 ring-indigo-600/15",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  warn: "bg-amber-50 text-amber-700 ring-amber-600/15",
  danger: "bg-rose-50 text-rose-700 ring-rose-600/15",
  info: "bg-sky-50 text-sky-700 ring-sky-600/15",
};

export function Badge({
  tone = "neutral",
  children,
  dot,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        BADGE[tone],
        className,
      )}
    >
      {dot && <span className={cx("h-1.5 w-1.5 rounded-full", DOT[tone])} />}
      {children}
    </span>
  );
}

const DOT: Record<Tone, string> = {
  neutral: "bg-slate-400",
  accent: "bg-indigo-500",
  success: "bg-emerald-500",
  warn: "bg-amber-500",
  danger: "bg-rose-500",
  info: "bg-sky-500",
};

export function StatusDot({ tone = "neutral", className }: { tone?: Tone; className?: string }): JSX.Element {
  return <span className={cx("inline-block h-2 w-2 rounded-full ring-2 ring-white", DOT[tone], className)} />;
}

// ── 버튼 ─────────────────────────────────────────────────────────────────────
const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-lg text-[13px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500";
export const btnPrimary = cx(BTN_BASE, "bg-indigo-600 px-3.5 py-2 text-white shadow-sm hover:bg-indigo-500");
export const btnGhost = cx(
  BTN_BASE,
  "border border-slate-200 bg-white px-3.5 py-2 text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50",
);
export const btnSubtle = cx(BTN_BASE, "bg-slate-100 px-2.5 py-1.5 text-[12px] text-slate-600 hover:bg-slate-200");
export const btnDanger = cx(BTN_BASE, "border border-rose-200 bg-white px-3 py-1.5 text-rose-600 hover:bg-rose-50");

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "subtle" | "danger" };
export function Btn({ variant = "primary", className, ...rest }: BtnProps): JSX.Element {
  const map = { primary: btnPrimary, ghost: btnGhost, subtle: btnSubtle, danger: btnDanger };
  return <button type="button" {...rest} className={cx(map[variant], className)} />;
}

// ── 폼 ───────────────────────────────────────────────────────────────────────
export const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

export function Field({
  label,
  children,
  hint,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <label className={cx("flex flex-col gap-1", className)}>
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={cx(inputCls, props.className)} />;
}
const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";
export function Select({ className, children, style, ...rest }: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return (
    <select
      {...rest}
      style={{ backgroundImage: CHEVRON, ...style }}
      className={cx(inputCls, "appearance-none bg-[length:16px_16px] bg-[right_0.6rem_center] bg-no-repeat pr-9", className)}
    >
      {children}
    </select>
  );
}
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={cx(inputCls, "min-h-[84px] resize-y leading-relaxed", props.className)} />;
}

// ── 테이블 (하이라인, 무거운 border 없음) ──────────────────────────────────────
export const thCls =
  "whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400";
export const tdCls = "px-3 py-2.5 text-[13px] text-slate-700 align-middle";
export const tdNum = "tnum whitespace-nowrap px-3 py-2.5 text-right text-[13px] text-slate-700 align-middle";

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white/50 px-6 py-14 text-center">
      {icon && <div className="mb-3 text-slate-300">{icon}</div>}
      <div className="text-sm font-semibold text-slate-700">{title}</div>
      {children && <div className="mt-1.5 max-w-md text-[13px] leading-relaxed text-slate-400">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Disclaimer({ children }: { children: ReactNode }): JSX.Element {
  return <p className="mt-2 text-[11px] leading-relaxed text-slate-400">{children}</p>;
}

// ── 아이콘 (인라인 SVG, 의존성 없음, stroke=currentColor) ──────────────────────
type IconProps = { className?: string };
const svg = (path: ReactNode) =>
  function Ico({ className = "h-[18px] w-[18px]" }: IconProps): JSX.Element {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        {path}
      </svg>
    );
  };

export const IconBoard = svg(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>);
export const IconUsers = svg(<><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M16 5.5a3 3 0 0 1 0 5.4" /><path d="M17.5 20a5.5 5.5 0 0 0-2.3-4.5" /></>);
export const IconFolder = svg(<><path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.2H19.5A1.5 1.5 0 0 1 21 9.7V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>);
export const IconSpark = svg(<><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></>);
export const IconDoc = svg(<><path d="M7 3h7l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M13 3v5h5M8.5 13h7M8.5 16.5h7" /></>);
export const IconFlag = svg(<><path d="M6 21V4M6 4h9l-1.5 3L15 10H6" /></>);
export const IconPin = svg(<><path d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10z" /><circle cx="12" cy="11" r="2.2" /></>);
export const IconChevron = svg(<path d="m9 6 6 6-6 6" />);
